/* eslint-disable @next/next/no-img-element */
import { mergeAttributes, Node, nodeInputRule } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer, NodeViewProps } from '@tiptap/react'

/**
 * Matches following attributes in Markdown-typed image: [, alt, src, title]
 *
 * Example:
 * ![Lorem](image.jpg) -> [, "Lorem", "image.jpg"]
 * ![](image.jpg "Ipsum") -> [, "", "image.jpg", "Ipsum"]
 * ![Lorem](image.jpg "Ipsum") -> [, "Lorem", "image.jpg", "Ipsum"]
 */
const IMAGE_INPUT_REGEX = /!\[(.+|:?)]\((\S+)(?:\s+["'](\S+)["'])?\)/

export type ImageNodeOptions = {
  inline?: boolean
  Component?: (props: NodeViewProps) => JSX.Element
}

// eslint-disable-next-line import/prefer-default-export
export const ImageNode = Node.create<ImageNodeOptions>({
  name: 'image',
  draggable: true,
  allowGapCursor: true,
  selectable: true,

  addOptions() {
    return {
      inline: false,
      Component: (props) => (
        <NodeViewWrapper data-node="image" className="image-component" contenteditable="false" data-drag-handle>
          {/* eslint-disable-next-line react/destructuring-assignment */}
          <img alt="" {...props.node.attrs} />
        </NodeViewWrapper>
      ),
    }
  },

  inline() {
    return this.options.inline
  },

  group() {
    return this.options.inline ? 'inline' : 'block'
  },

  addAttributes: () => ({
    src: { default: null },
    alt: { default: null },
    title: { default: null },
    filename: { default: null },
  }),

  addInputRules() {
    return [
      nodeInputRule({
        find: IMAGE_INPUT_REGEX,
        type: this.type,
        getAttributes: (match) => {
          const [, alt, src, title] = match
          return {
            src,
            alt,
            title,
          }
        },
      }),
    ]
  },

  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => this.editor.commands.createParagraphNear(),
    }
  },

  parseHTML() {
    return [
      {
        tag: 'image-node',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['image-node', mergeAttributes(HTMLAttributes)]
  },

  addNodeView() {
    return ReactNodeViewRenderer(this.options.Component)
  },
})
